import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  getMyApplications,
  getReceivedApplications,
  updateApplication,
  RequirementApplication,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import {
  MessageSquare, Users, Clock, CheckCircle, XCircle,
  RotateCcw, Send, Inbox, User, Phone, Check, X
} from "lucide-react";
import { fadeUp, formatDate, formatINR } from "./MarketplaceShared";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import {
  APP_STATUS_COLOR,
  MARKETPLACE_GRADIENT,
  ORDER_APPLICATION_STATUS_LABELS,
  RECEIVED_APPLICATION_STATUS_LABELS,
} from "../../organisms/Dashboard/Marketplace/marketplaceConstants";

type QuoteTabType = "applied" | "received";

const MarketplaceMyOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const isLoggedIn = !!localStorage.getItem("access_token");

  const [tab, setTab] = useState<QuoteTabType>("applied");
  const [myApplications, setMyApplications] = useState<RequirementApplication[]>([]);
  const [receivedApplications, setReceivedApplications] = useState<RequirementApplication[]>([]);
  const [loadingApplied, setLoadingApplied] = useState(false);
  const [loadingReceived, setLoadingReceived] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<string | null>(null);

  const fetchApplied = useCallback(async () => {
    setLoadingApplied(true);
    try {
      const res = await getMyApplications({ page_size: 50 });
      setMyApplications(res.results ?? []);
    } catch {
      setMyApplications([]);
    } finally {
      setLoadingApplied(false);
    }
  }, []);

  const fetchReceived = useCallback(async () => {
    setLoadingReceived(true);
    try {
      const res = await getReceivedApplications({ page_size: 50 });
      setReceivedApplications(res.results ?? []);
    } catch {
      setReceivedApplications([]);
    } finally {
      setLoadingReceived(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/marketplace");
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      }, 100);
      return;
    }
    fetchApplied();
    fetchReceived();
  }, [isLoggedIn, navigate, fetchApplied, fetchReceived]);

  const handleAction = async (appId: string, status: "accepted" | "rejected") => {
    setActionLoadingId(appId);
    try {
      await updateApplication(appId, {
        status,
        reject_other_pending: status === "accepted",
        fulfill_requirement: status === "accepted",
      });
      showSuccessToast(
        status === "accepted"
          ? "✅ Quote bid accepted! Requirement updated."
          : "Quote bid rejected."
      );
      fetchReceived();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Failed to update status.";
      showErrorToast(msg);
    } finally {
      setActionLoadingId(null);
    }
  };

  const loading = tab === "applied" ? loadingApplied : loadingReceived;

  return (
    <PageWrap>
      <PageInner>
        <TopRow>
          <PageTitle>Custom Quotes</PageTitle>
          <PageSubtitle>
            Track quote bids you have submitted and manage applications received on your posted requirements.
          </PageSubtitle>
        </TopRow>

        <TabStrip>
          <TabBtn $active={tab === "applied"} onClick={() => setTab("applied")}>
            <Send size={15} />
            My Submitted Quotes
            {myApplications.length > 0 && <TabCount>{myApplications.length}</TabCount>}
          </TabBtn>
          <TabBtn $active={tab === "received"} onClick={() => setTab("received")}>
            <Users size={15} />
            Received Applications
            {receivedApplications.length > 0 && <TabCount>{receivedApplications.length}</TabCount>}
          </TabBtn>
        </TabStrip>

        {loading ? (
          <SpinnerWrap><Spinner /></SpinnerWrap>
        ) : tab === "applied" ? (
          myApplications.length === 0 ? (
            <EmptyState>
              <Send size={56} strokeWidth={1} color="rgba(23,23,23,0.15)" />
              <EmptyTitle>No submitted quotes yet</EmptyTitle>
              <EmptyDesc>Browse custom unit requirements and project quote requests on the marketplace and submit your bids.</EmptyDesc>
              <BrowseBtn onClick={() => navigate("/marketplace")}>Browse Open Quotes</BrowseBtn>
            </EmptyState>
          ) : (
            <CardGrid>
              {myApplications.map(app => (
                <AppCard key={app.id}>
                  <CardHeader>
                    <TypeRow>
                      <Users size={15} color="#c94daa" />
                      <TypeLabel>
                        {app.requirement_type === "equipment_requirement"
                          ? "Equipment Need"
                          : app.requirement_type === "bulk_custom_unit"
                          ? "Custom Unit / Bulk Quote"
                          : "Service Need"}
                      </TypeLabel>
                    </TypeRow>
                    <StatusPill $color={APP_STATUS_COLOR[app.status] ?? "#6b7280"}>
                      {app.status === "accepted" ? <CheckCircle size={11} /> :
                       app.status === "rejected" ? <XCircle size={11} /> :
                       app.status === "withdrawn" ? <RotateCcw size={11} /> :
                       <Clock size={11} />}
                      {ORDER_APPLICATION_STATUS_LABELS[app.status] ?? app.status}
                    </StatusPill>
                  </CardHeader>

                  <CardTitle>{app.requirement_title ?? "Custom Unit Requirement"}</CardTitle>

                  {app.message && (
                    <PitchRow>
                      <MessageSquare size={13} color="rgba(23,23,23,0.4)" />
                      <span>"{app.message}"</span>
                    </PitchRow>
                  )}

                  {app.proposed_rate && (
                    <CardMeta>
                      <MetaChip $highlight>
                        Proposed: {formatINR(app.proposed_rate)}
                      </MetaChip>
                    </CardMeta>
                  )}

                  {app.owner_notes && (
                    <OwnerNote>
                      <strong>Client Note:</strong> {app.owner_notes}
                    </OwnerNote>
                  )}

                  {/* Status hint explanation */}
                  <AppStatusHint $status={app.status}>
                    {app.status === "pending" && "⏳ Your quote proposal is under review by the requirement poster."}
                    {app.status === "accepted" && "🎉 Congratulations! Your quote bid was accepted. The client will reach out soon."}
                    {app.status === "rejected" && "Your quote bid was not selected this time. Keep bidding!"}
                    {app.status === "withdrawn" && "You withdrew this proposal."}
                  </AppStatusHint>

                  <CardFooter>
                    <RefLabel style={{ opacity: 0.4, fontSize: 11 }}>ID: {app.id.slice(0, 8)}…</RefLabel>
                    <DateLabel>
                      <Clock size={11} />
                      Applied {formatDate(app.created_at)}
                    </DateLabel>
                  </CardFooter>
                </AppCard>
              ))}
            </CardGrid>
          )
        ) : (
          receivedApplications.length === 0 ? (
            <EmptyState>
              <Inbox size={56} strokeWidth={1} color="rgba(23,23,23,0.15)" />
              <EmptyTitle>No received applications yet</EmptyTitle>
              <EmptyDesc>When studios or professionals submit bids on your custom unit quote requests, they'll appear here.</EmptyDesc>
              <BrowseBtn onClick={() => navigate("/marketplace/team")}>Build a Custom Unit</BrowseBtn>
            </EmptyState>
          ) : (
            <CardGrid>
              {receivedApplications.map(app => (
                <AppCard key={app.id}>
                  <CardHeader>
                    <TypeRow>
                      <User size={15} color="#c94daa" />
                      <TypeLabel>{app.applicant_name || `Applicant #${app.applicant}`}</TypeLabel>
                    </TypeRow>
                    <StatusPill $color={APP_STATUS_COLOR[app.status] ?? "#6b7280"}>
                      {app.status === "accepted" ? <CheckCircle size={11} /> :
                       app.status === "rejected" ? <XCircle size={11} /> :
                       <Clock size={11} />}
                      {RECEIVED_APPLICATION_STATUS_LABELS[app.status] ?? app.status}
                    </StatusPill>
                  </CardHeader>

                  <CardTitle>{app.requirement_title ?? "Custom Unit Requirement"}</CardTitle>

                  {app.applicant_phone && (
                    <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "rgba(23, 23, 23, 0.6)" }}>
                      <Phone size={12} color="#c94daa" /> {app.applicant_phone}
                    </div>
                  )}

                  {app.message && (
                    <PitchRow>
                      <MessageSquare size={13} color="rgba(23,23,23,0.4)" />
                      <span>"{app.message}"</span>
                    </PitchRow>
                  )}

                  {app.proposed_rate && (
                    <CardMeta>
                      <MetaChip $highlight>
                        Bid Rate: {formatINR(app.proposed_rate)}
                      </MetaChip>
                    </CardMeta>
                  )}

                  {/* Actions for pending applications */}
                  {app.status === "pending" && (
                    <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
                      <ActionBtnPrimary
                        onClick={() => handleAction(app.id, "accepted")}
                        disabled={actionLoadingId === app.id}
                      >
                        <Check size={13} /> {actionLoadingId === app.id ? "…" : "Accept Bid"}
                      </ActionBtnPrimary>
                      <ActionBtnSecondary
                        onClick={() => handleAction(app.id, "rejected")}
                        disabled={actionLoadingId === app.id}
                      >
                        <X size={13} /> {actionLoadingId === app.id ? "…" : "Reject"}
                      </ActionBtnSecondary>
                    </div>
                  )}

                  {app.owner_notes && (
                    <OwnerNote>
                      <strong>Your Note:</strong> {app.owner_notes}
                    </OwnerNote>
                  )}

                  <CardFooter>
                    <RefLabel style={{ opacity: 0.4, fontSize: 11 }}>ID: {app.id.slice(0, 8)}…</RefLabel>
                    <DateLabel>
                      <Clock size={11} />
                      Received {formatDate(app.created_at)}
                    </DateLabel>
                  </CardFooter>
                </AppCard>
              ))}
            </CardGrid>
          )
        )}
      </PageInner>
    </PageWrap>
  );
};

export default MarketplaceMyOrdersPage;

// ─── Styled ───────────────────────────────────────────────────────────────────

const PageWrap = styled.div`
  min-height: 100vh;
  background: #f7f8fc;
  font-family: Urbanist, sans-serif;
`;

const PageInner = styled.div`
  max-width: 1680px;
  margin: 0 auto;
  padding: 32px 40px 80px;
  animation: ${fadeUp} 0.35s ease;

  @media (max-width: 640px) { padding: 20px 14px 60px; }
`;

const TopRow = styled.div`margin-bottom: 28px;`;

const PageTitle = styled.h1`
  font-size: 26px; font-weight: 800; color: #171717;
  margin: 0 0 6px;
  background: ${MARKETPLACE_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const PageSubtitle = styled.p`
  font-family: Montserrat, sans-serif;
  font-size: 13px; color: rgba(23,23,23,0.5); margin: 0;
`;

const TabStrip = styled.div`
  display: flex; gap: 4px;
  background: white;
  border: 1px solid rgba(23,23,23,0.08);
  border-radius: 14px;
  padding: 5px;
  margin-bottom: 24px;
  max-width: 480px;
`;

const TabBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex; align-items: center; justify-content: center; gap: 7px;
  padding: 10px 16px; border-radius: 10px; border: none; cursor: pointer;
  font-family: Urbanist, sans-serif; font-size: 13px; font-weight: 700;
  transition: all 0.2s;
  background: ${p => p.$active ? MARKETPLACE_GRADIENT : "transparent"};
  color: ${p => p.$active ? "white" : "rgba(23,23,23,0.45)"};
  box-shadow: ${p => p.$active ? "0 4px 12px rgba(201,77,170,0.25)" : "none"};
`;

const TabCount = styled.span`
  background: rgba(255,255,255,0.25);
  border-radius: 100px; padding: 1px 7px;
  font-size: 11px; font-weight: 700;
  font-family: Montserrat, sans-serif;
`;

const SpinnerWrap = styled.div`display: flex; justify-content: center; padding: 80px 0;`;

const Spinner = styled.div`
  width: 40px; height: 40px;
  border: 3px solid rgba(23,23,23,0.08);
  border-top-color: #c94daa;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center;
  gap: 14px; padding: 80px 24px;
  background: white; border-radius: 20px;
  border: 1px solid rgba(23,23,23,0.07);
  box-shadow: 0 4px 20px rgba(23,23,23,0.04);
`;

const EmptyTitle = styled.h3`
  font-size: 18px; font-weight: 800; color: #171717; margin: 0;
`;

const EmptyDesc = styled.p`
  font-family: Montserrat, sans-serif; font-size: 13px;
  color: rgba(23,23,23,0.45); text-align: center; max-width: 380px; margin: 0;
`;

const BrowseBtn = styled.button`
  padding: 10px 24px; border-radius: 10px; border: none; cursor: pointer;
  background: ${MARKETPLACE_GRADIENT}; color: white;
  font-family: Urbanist, sans-serif; font-size: 14px; font-weight: 700;
  box-shadow: 0 4px 14px rgba(201,77,170,0.25);
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 7px 20px rgba(201,77,170,0.35); }
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
`;

const AppCard = styled.div`
  background: white; border-radius: 18px;
  border: 1px solid rgba(23,23,23,0.07);
  box-shadow: 0 4px 20px rgba(23,23,23,0.05);
  padding: 20px;
  display: flex; flex-direction: column; gap: 10px;
  animation: ${fadeUp} 0.3s ease;
  transition: box-shadow 0.2s, transform 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(23,23,23,0.09); }
`;

const CardHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
`;

const TypeRow = styled.div`display: flex; align-items: center; gap: 6px;`;

const TypeLabel = styled.span`
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 700;
  color: rgba(23,23,23,0.5); text-transform: uppercase; letter-spacing: 0.04em;
`;

const StatusPill = styled.span<{ $color: string }>`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 100px;
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 700;
  background: ${p => p.$color}18; color: ${p => p.$color};
`;

const CardTitle = styled.h3`
  font-size: 16px; font-weight: 800; color: #171717; margin: 0;
  line-height: 1.3;
`;

const CardMeta = styled.div`display: flex; flex-wrap: wrap; gap: 7px; align-items: center;`;

const MetaChip = styled.span<{ $highlight?: boolean }>`
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 100px;
  background: ${p => p.$highlight ? "rgba(201,77,170,0.08)" : "#f1f3f8"};
  color: ${p => p.$highlight ? "#c94daa" : "rgba(23,23,23,0.55)"};
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 700;
`;

const CardFooter = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding-top: 8px; border-top: 1px solid rgba(23,23,23,0.06);
`;

const RefLabel = styled.span`
  font-family: Montserrat, sans-serif; font-size: 11px; font-weight: 600;
  color: rgba(23,23,23,0.35);
`;

const DateLabel = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-family: Montserrat, sans-serif; font-size: 11px; color: rgba(23,23,23,0.35);
`;

const OwnerNote = styled.div`
  background: #f7f8fc; border-left: 3px solid #c94daa;
  padding: 8px 12px; border-radius: 0 8px 8px 0;
  font-family: Montserrat, sans-serif; font-size: 12px; color: rgba(23,23,23,0.65);
  font-style: italic;
`;

const PitchRow = styled.div`
  display: flex; align-items: flex-start; gap: 7px;
  font-family: Montserrat, sans-serif; font-size: 12px;
  color: rgba(23,23,23,0.6); font-style: italic;
`;

const AppStatusHint = styled.div<{ $status: string }>`
  padding: 10px 12px; border-radius: 10px;
  font-family: Montserrat, sans-serif; font-size: 12px; line-height: 1.5;
  background: ${p =>
    p.$status === "accepted" ? "#16a34a12" :
    p.$status === "rejected" ? "#ef444412" :
    p.$status === "withdrawn" ? "#6b728012" : "#f09a3e10"};
  color: ${p =>
    p.$status === "accepted" ? "#16a34a" :
    p.$status === "rejected" ? "#ef4444" :
    p.$status === "withdrawn" ? "#6b7280" : "#a06010"};
`;

const ActionBtnPrimary = styled.button`
  flex: 1;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 14px; border-radius: 10px; border: none; cursor: pointer;
  background: #16a34a; color: white;
  font-family: Urbanist, sans-serif; font-size: 12px; font-weight: 700;
  transition: opacity 0.2s;
  &:hover:not(:disabled) { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ActionBtnSecondary = styled.button`
  flex: 1;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 8px 14px; border-radius: 10px; border: 1px solid rgba(239, 68, 68, 0.3); cursor: pointer;
  background: rgba(239, 68, 68, 0.08); color: #ef4444;
  font-family: Urbanist, sans-serif; font-size: 12px; font-weight: 700;
  transition: background 0.2s;
  &:hover:not(:disabled) { background: rgba(239, 68, 68, 0.15); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
